/**
 * Agency portal — Compare (SYR-85).
 *
 * Client-vs-client leaderboard with bucket filtering, a pinned platform
 * benchmark row when the view is a single business × product bucket, and
 * per-element meters showing where each client leads. Portfolios are
 * mock-homogeneous, so rankings are always coherent.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAgencyPortfolioAnalyticsQuery,
    type IAgencyAnalyticsBrand,
    type IAgencyAnalyticsClient,
} from '../../services/nestjs-base-api';
import { THEME } from '../../components/MarketerView/config/theme';
import {
    METER_GRADIENT,
    METER_GRADIENT_NEGATIVE,
    bandFromClassification,
    formatBipolarScore,
    gaugePosition,
} from '../../components/MarketerView/config/bandColours';
import { colourForScore } from '../../components/MarketerView/config/elementColours';
import { BandTag, Card } from '../../components/MarketerView/atoms';
import { useAgencyScope, AGENCY_PERIOD_LABELS } from '../shell/AgencyScopeContext';
import { AgencyEyebrow, AgencyPageHeader, DeltaChip } from '../components/agencyUi';

interface CompareRow {
    client: IAgencyAnalyticsClient;
    brand: IAgencyAnalyticsBrand;
}

const bucketKey = (b: IAgencyAnalyticsBrand): string =>
    `${b.bucket.businessTypeSlug ?? '?'}|${b.bucket.productTypeSlug ?? '?'}`;

const bucketLabel = (b: IAgencyAnalyticsBrand): string =>
    [b.bucket.businessTypeName, b.bucket.productTypeName].filter(Boolean).join(' × ') ||
    '—';

/** Short initials used to identify each client on the meters. */
const initials = (name: string): string =>
    name
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

export const AgencyComparePage: React.FunctionComponent = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { period } = useAgencyScope();
    const [bucketFilter, setBucketFilter] = useState<string | null>(null);

    const { data, isLoading } = useGetAgencyPortfolioAnalyticsQuery({
        jwtToken: user?.jwtToken || '',
        period,
    });

    const rows = useMemo<CompareRow[]>(() => {
        const flat: CompareRow[] = [];
        for (const client of data?.clients || []) {
            for (const brand of client.brands) flat.push({ client, brand });
        }
        return flat;
    }, [data]);

    const buckets = useMemo(() => {
        const map = new Map<string, string>();
        for (const r of rows) map.set(bucketKey(r.brand), bucketLabel(r.brand));
        return Array.from(map.entries());
    }, [rows]);

    const visible = useMemo(
        () =>
            (bucketFilter
                ? rows.filter((r) => bucketKey(r.brand) === bucketFilter)
                : rows
            ).slice(),
        [rows, bucketFilter],
    );

    const scored = visible
        .filter((r) => r.brand.equity.score !== null)
        .sort((a, b) => (b.brand.equity.score ?? 0) - (a.brand.equity.score ?? 0));
    const unscored = visible.filter((r) => r.brand.equity.score === null);

    // Element order comes from the first scored brand (API config order).
    const elementNames = scored[0]?.brand.equity.elements.map((e) => e.element) || [];

    // Benchmark context is shown when every visible brand shares one bucket.
    const singleBucket =
        visible.length > 0 && visible.every((r) => bucketKey(r.brand) === bucketKey(visible[0].brand));
    const benchmarkSource = singleBucket
        ? scored.find((r) => r.brand.benchmark?.available)?.brand.benchmark
        : undefined;

    const benchmarkElementMean = (element: string): number | undefined =>
        benchmarkSource?.elementBenchmarks?.find(
            (eb) => eb.level === 1 && eb.categoryName === element,
        )?.industryMean;

    const periodLabel = intl.formatMessage({
        id: AGENCY_PERIOD_LABELS[period].id,
        defaultMessage: AGENCY_PERIOD_LABELS[period].def,
    });

    const chipStyle = (active: boolean): React.CSSProperties => ({
        padding: '6px 14px',
        borderRadius: 999,
        border: `1px solid ${active ? THEME.primary : THEME.border}`,
        background: active ? THEME.primarySoft : THEME.bgChip,
        color: active ? THEME.primaryDark : THEME.textBase,
        fontWeight: active ? 600 : 500,
        fontSize: 13,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    });

    if (isLoading) {
        return (
            <div>
                <AgencyPageHeader
                    title={<FormattedMessage id="agency.compare.title" defaultMessage="Compare" />}
                />
                <div style={{ padding: 96, textAlign: 'center' }}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    return (
        <div>
            <AgencyPageHeader
                title={<FormattedMessage id="agency.compare.title" defaultMessage="Compare" />}
                subtitle={
                    <FormattedMessage
                        id="agency.compare.subtitle"
                        defaultMessage="Rank your clients side by side and see where each one leads — {period}."
                        values={{ period: periodLabel.toLowerCase() }}
                    />
                }
            />

            {/* Bucket filter — hidden when the portfolio is one bucket anyway */}
            {buckets.length > 1 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                    <button
                        type="button"
                        style={chipStyle(bucketFilter === null)}
                        onClick={() => setBucketFilter(null)}
                    >
                        {intl.formatMessage({
                            id: 'agency.compare.allBuckets',
                            defaultMessage: 'All segments',
                        })}
                    </button>
                    {buckets.map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            style={chipStyle(bucketFilter === key)}
                            onClick={() => setBucketFilter(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {/* Leaderboard */}
            <Card pad={0} style={{ marginBottom: 28 }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                        <thead>
                            <tr>
                                <th style={thStyle({ textAlign: 'left', paddingLeft: 24 })}>
                                    <FormattedMessage
                                        id="agency.compare.colClient"
                                        defaultMessage="Client"
                                    />
                                </th>
                                <th style={thStyle({})}>
                                    <FormattedMessage
                                        id="agency.compare.colEquity"
                                        defaultMessage="Brand equity"
                                    />
                                </th>
                                {elementNames.map((name) => (
                                    <th key={name} style={thStyle({})}>
                                        {name}
                                    </th>
                                ))}
                                <th style={thStyle({ paddingRight: 24 })}>
                                    <FormattedMessage
                                        id="agency.compare.colResponses"
                                        defaultMessage="Responses"
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {scored.map(({ client, brand }, idx) => (
                                <tr
                                    key={brand.brandId}
                                    onClick={() =>
                                        navigate(`/agency/clients/${client.clientOrganisationId}`)
                                    }
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLTableRowElement).style.background =
                                            THEME.bgTinted;
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLTableRowElement).style.background =
                                            'transparent';
                                    }}
                                >
                                    <td style={tdStyle({ textAlign: 'left', paddingLeft: 24 })}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: 800,
                                                    color: THEME.textMuted,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    minWidth: 22,
                                                }}
                                            >
                                                #{idx + 1}
                                            </span>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: 14 }}>
                                                    {client.clientOrganisationName}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: 11.5,
                                                        color: THEME.textMuted,
                                                    }}
                                                >
                                                    {bucketLabel(brand)}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={tdStyle({})}>
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 18,
                                                    fontWeight: 800,
                                                    color: colourForScore(
                                                        'Brand Equity',
                                                        brand.equity.score,
                                                    ),
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {formatBipolarScore(brand.equity.score)}
                                            </span>
                                            <DeltaChip delta={brand.equity.delta} />
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <BandTag
                                                classification={
                                                    brand.equity.classificationLabel ?? null
                                                }
                                                band={bandFromClassification(
                                                    brand.equity.classificationLabel ?? null,
                                                )}
                                            />
                                        </div>
                                    </td>
                                    {elementNames.map((name) => {
                                        const el = brand.equity.elements.find(
                                            (e) => e.element === name,
                                        );
                                        if (!el || el.locked) {
                                            return (
                                                <td key={name} style={tdStyle({})}>
                                                    <LockOutlined
                                                        style={{ color: THEME.textMuted }}
                                                    />
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={name} style={tdStyle({})}>
                                                <Tooltip
                                                    title={el.classificationLabel || undefined}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: 15,
                                                            fontWeight: 700,
                                                            color: colourForScore(
                                                                name,
                                                                el.score,
                                                            ),
                                                            fontVariantNumeric: 'tabular-nums',
                                                        }}
                                                    >
                                                        {formatBipolarScore(el.score)}
                                                    </span>
                                                </Tooltip>
                                            </td>
                                        );
                                    })}
                                    <td
                                        style={tdStyle({
                                            paddingRight: 24,
                                            color: THEME.textSecondary,
                                            fontVariantNumeric: 'tabular-nums',
                                        })}
                                    >
                                        {brand.equity.totalResponses.toLocaleString()}
                                    </td>
                                </tr>
                            ))}

                            {/* Pinned platform benchmark row (single-bucket view) */}
                            {benchmarkSource?.distribution && (
                                <tr style={{ background: THEME.bgTinted }}>
                                    <td
                                        style={tdStyle({
                                            textAlign: 'left',
                                            paddingLeft: 24,
                                            borderTop: `2px solid ${THEME.border}`,
                                        })}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 13,
                                                color: THEME.textSecondary,
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            <FormattedMessage
                                                id="agency.compare.benchmarkRow"
                                                defaultMessage="Segment benchmark"
                                            />
                                        </div>
                                        <div style={{ fontSize: 11.5, color: THEME.textMuted }}>
                                            <FormattedMessage
                                                id="agency.compare.benchmarkPool"
                                                defaultMessage="{n} brands"
                                                values={{
                                                    n: benchmarkSource.distribution.sampleSize,
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td
                                        style={tdStyle({
                                            borderTop: `2px solid ${THEME.border}`,
                                            color: THEME.textSecondary,
                                            fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                        })}
                                    >
                                        {formatBipolarScore(benchmarkSource.distribution.mean)}
                                    </td>
                                    {elementNames.map((name) => {
                                        const mean = benchmarkElementMean(name);
                                        return (
                                            <td
                                                key={name}
                                                style={tdStyle({
                                                    borderTop: `2px solid ${THEME.border}`,
                                                    color: THEME.textSecondary,
                                                    fontWeight: 600,
                                                    fontVariantNumeric: 'tabular-nums',
                                                })}
                                            >
                                                {mean !== undefined
                                                    ? formatBipolarScore(mean)
                                                    : '—'}
                                            </td>
                                        );
                                    })}
                                    <td
                                        style={tdStyle({
                                            paddingRight: 24,
                                            borderTop: `2px solid ${THEME.border}`,
                                        })}
                                    />
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {unscored.length > 0 && (
                    <div
                        style={{
                            padding: '12px 24px',
                            borderTop: `1px solid ${THEME.borderSubtle}`,
                            fontSize: 13,
                            color: THEME.textSecondary,
                        }}
                    >
                        <FormattedMessage
                            id="agency.compare.unscored"
                            defaultMessage="Not enough data yet: {names}"
                            values={{
                                names: unscored
                                    .map((r) => r.client.clientOrganisationName)
                                    .join(', '),
                            }}
                        />
                    </div>
                )}
            </Card>

            {/* Element meters — who leads where */}
            {scored.length > 1 && (
                <>
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.compare.metersTitle"
                            defaultMessage="Where each client leads"
                        />
                    </AgencyEyebrow>
                    <Card>
                        {/* Legend */}
                        <div
                            style={{
                                display: 'flex',
                                gap: 10,
                                flexWrap: 'wrap',
                                marginBottom: 22,
                            }}
                        >
                            {scored.map(({ client }) => (
                                <span
                                    key={client.clientOrganisationId}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 7,
                                        fontSize: 12.5,
                                        color: THEME.textSecondary,
                                        fontWeight: 600,
                                    }}
                                >
                                    <MeterPin label={initials(client.clientOrganisationName)} small />
                                    {client.clientOrganisationName}
                                </span>
                            ))}
                            {benchmarkSource?.distribution && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 7,
                                        fontSize: 12.5,
                                        color: THEME.textSecondary,
                                        fontWeight: 600,
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 3,
                                            height: 14,
                                            background: THEME.textBase,
                                            borderRadius: 2,
                                        }}
                                    />
                                    <FormattedMessage
                                        id="agency.compare.benchmarkLegend"
                                        defaultMessage="Segment average"
                                    />
                                </span>
                            )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
                            {/* Overall BE meter first, then each element */}
                            <MeterRow
                                label={intl.formatMessage({
                                    id: 'agency.compare.meterOverall',
                                    defaultMessage: 'Brand equity',
                                })}
                                entries={scored.map(({ client, brand }) => ({
                                    key: client.clientOrganisationId,
                                    label: initials(client.clientOrganisationName),
                                    tooltip: `${client.clientOrganisationName}: ${formatBipolarScore(
                                        brand.equity.score,
                                    )}`,
                                    value: brand.equity.score,
                                }))}
                                mean={benchmarkSource?.distribution?.mean}
                            />
                            {elementNames.map((name) => (
                                <MeterRow
                                    key={name}
                                    label={name}
                                    entries={scored.map(({ client, brand }) => {
                                        const el = brand.equity.elements.find(
                                            (e) => e.element === name,
                                        );
                                        return {
                                            key: client.clientOrganisationId,
                                            label: initials(client.clientOrganisationName),
                                            tooltip: `${client.clientOrganisationName}: ${formatBipolarScore(
                                                el?.locked ? null : el?.score ?? null,
                                            )}`,
                                            value: el?.locked ? null : el?.score ?? null,
                                        };
                                    })}
                                    mean={benchmarkElementMean(name)}
                                    negativeScale={name === 'Price Deals'}
                                />
                            ))}
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
};

const thStyle = (extra: React.CSSProperties): React.CSSProperties => ({
    padding: '14px 14px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: THEME.textMuted,
    borderBottom: `1px solid ${THEME.border}`,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    ...extra,
});

const tdStyle = (extra: React.CSSProperties): React.CSSProperties => ({
    padding: '16px 14px',
    borderBottom: `1px solid ${THEME.borderSubtle}`,
    textAlign: 'center',
    verticalAlign: 'middle',
    ...extra,
});

/** Navy pin with client initials, placed on the gradient meters. */
const MeterPin: React.FunctionComponent<{ label: string; small?: boolean }> = ({
    label,
    small,
}) => (
    <span
        style={{
            width: small ? 20 : 26,
            height: small ? 20 : 26,
            borderRadius: '50%',
            background: '#0F172A',
            color: '#fff',
            fontSize: small ? 9 : 10,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 0 2px #fff, 0 3px 8px rgba(15,23,42,0.25)',
            letterSpacing: '0.02em',
            flexShrink: 0,
        }}
    >
        {label}
    </span>
);

interface MeterEntry {
    key: number;
    label: string;
    tooltip: string;
    value: number | null;
}

const MeterRow: React.FunctionComponent<{
    label: string;
    entries: MeterEntry[];
    mean?: number;
    /** Price Deals: -100..0 scale with zero at the right edge. */
    negativeScale?: boolean;
}> = ({ label, entries, mean, negativeScale = false }) => {
    const present = entries.filter((e) => e.value !== null);
    const position = (value: number | null | undefined): number =>
        negativeScale
            ? Math.max(0, Math.min(100, (value ?? 0) + 100))
            : gaugePosition(value);
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 10,
                }}
            >
                <span style={{ fontSize: 13.5, fontWeight: 700, color: THEME.textBase }}>
                    {label}
                </span>
                {present.length > 0 && (
                    <span style={{ fontSize: 12, color: THEME.textMuted }}>
                        {(() => {
                            const leader = present.reduce((a, b) =>
                                (a.value ?? -101) >= (b.value ?? -101) ? a : b,
                            );
                            return (
                                <FormattedMessage
                                    id="agency.compare.leads"
                                    defaultMessage="{name} leads"
                                    values={{ name: leader.tooltip.split(':')[0] }}
                                />
                            );
                        })()}
                    </span>
                )}
            </div>
            <div style={{ position: 'relative', height: 30 }}>
                <div
                    style={{
                        position: 'absolute',
                        top: 11,
                        left: 0,
                        right: 0,
                        height: 8,
                        borderRadius: 8,
                        background: negativeScale ? METER_GRADIENT_NEGATIVE : METER_GRADIENT,
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
                    }}
                />
                {/* zero marker */}
                <div
                    style={{
                        position: 'absolute',
                        top: 8,
                        left: negativeScale ? '100%' : '50%',
                        width: 1.5,
                        height: 14,
                        background: 'rgba(15,23,42,0.35)',
                        transform: negativeScale ? 'translateX(-100%)' : undefined,
                    }}
                />
                {mean !== undefined && (
                    <div
                        title={`Segment average: ${formatBipolarScore(mean)}`}
                        style={{
                            position: 'absolute',
                            top: 5,
                            left: `${position(mean)}%`,
                            transform: 'translateX(-50%)',
                            width: 3,
                            height: 20,
                            background: THEME.textBase,
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        }}
                    />
                )}
                {present.map((e) => (
                    <span
                        key={e.key}
                        title={e.tooltip}
                        style={{
                            position: 'absolute',
                            top: 2,
                            left: `${position(e.value)}%`,
                            transform: 'translateX(-50%)',
                        }}
                    >
                        <MeterPin label={e.label} />
                    </span>
                ))}
            </div>
        </div>
    );
};
